import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { contractsClient } from '../../Delivery/Registries/Contracts/api';
export function ProgramFollowOn({ landscapeId, reference }) {
    const { user } = useAuth();
    const location = useLocation();
    const client = useQueryClient();
    const context = {
        landscapeId,
        libraryItemId: reference.libraryItemId,
        releaseId: reference.releaseId,
        releaseVersion: reference.releaseVersion
    };
    const options = useQuery({
        queryKey: [
            'program-follow-on-options',
            user?.tenant_id,
            user?.id,
            context
        ],
        queryFn: ()=>contractsClient.listProgramFollowOnOptions(context)
    });
    const [mode, setMode] = useState(null);
    const [title, setTitle] = useState(`Follow-on: ${reference.title}`);
    const [target, setTarget] = useState('');
    const [review, setReview] = useState(false);
    // A frozen confirmed request survives errors and remounts. Editing is an
    // explicit new decision and receives a new retry key, never a silent retry.
    const storageKey = `program-follow-on:${user?.tenant_id}:${user?.id}:${landscapeId}:${reference.releaseId}`;
    const [pending, setPending] = useState(()=>{
        try {
            const saved = sessionStorage.getItem(storageKey);
            return saved ? JSON.parse(saved) : null;
        } catch  {
            return null;
        }
    });
    const save = useMutation({
        mutationFn: (request)=>contractsClient.saveProgramFollowOn(request),
        onSuccess: ()=>{
            sessionStorage.removeItem(storageKey);
            setPending(null);
            void client.invalidateQueries({
                queryKey: [
                    'program-follow-on-options'
                ]
            });
            // Saving changes membership, not the publication. Refetching references
            // here unmounts this form and discards the acknowledged save result.
            void client.invalidateQueries({
                queryKey: [
                    'landscapes'
                ],
                predicate: (query)=>query.queryKey[1] !== 'program-references'
            });
        }
    });
    const selected = options.data?.opportunities.find((o)=>o.opportunityId === target);
    const confirm = ()=>{
        const request = pending ?? {
            ...context,
            retryKey: crypto.randomUUID(),
            title: mode === 'link' ? selected?.title ?? '' : title,
            opportunityId: mode === 'link' ? target : ''
        };
        sessionStorage.setItem(storageKey, JSON.stringify(request));
        setPending(request);
        save.mutate(request);
    };
    if (save.data?.opportunity) return /*#__PURE__*/ _jsxs("output", {
        children: [
            "Opportunity saved:",
            ' ',
            /*#__PURE__*/ _jsx(Link, {
                to: `/opportunities?watchOpportunity=${encodeURIComponent(save.data.opportunity.opportunityId)}`,
                state: {
                    returnTo: location.pathname + location.search
                },
                children: save.data.opportunity.title
            }),
            ". A Pursuit requires the normal explicit action."
        ]
    });
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Program follow-on",
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Existing authorized Program follow-on threads (across Landscapes):"
            }),
            options.isLoading && /*#__PURE__*/ _jsx("output", {
                children: "Loading authorized Opportunities…"
            }),
            options.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Could not load authorized Opportunities.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void options.refetch(),
                        children: "Retry Opportunities"
                    })
                ]
            }),
            !options.isFetching && !options.isError && options.data?.opportunities.filter((o)=>o.alreadyLinked).map((o)=>/*#__PURE__*/ _jsxs(Link, {
                    className: "block",
                    to: `/opportunities?watchOpportunity=${encodeURIComponent(o.opportunityId)}`,
                    state: {
                        returnTo: location.pathname + location.search
                    },
                    children: [
                        o.title,
                        " · Already linked"
                    ]
                }, o.opportunityId)),
            !mode && !pending && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.landscapes.programFollowOnCreate,
                        disabled: !options.data || options.isFetching || options.isError,
                        onClick: ()=>{
                            setMode('create');
                            setReview(true);
                        },
                        children: "Create Opportunity"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.landscapes.programFollowOnLink,
                        disabled: !options.data || options.isFetching || options.isError,
                        onClick: ()=>setMode('link'),
                        children: "Link Existing Opportunity"
                    })
                ]
            }),
            (mode || pending) && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Review Program context: ",
                            reference.summary
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Program ",
                            reference.programId,
                            " · Library release ",
                            reference.releaseId,
                            " · Version",
                            ' ',
                            reference.releaseVersion,
                            ". End date is not a confirmed recompete date. This saves an Opportunity reference only; a Pursuit requires a separate explicit action."
                        ]
                    }),
                    pending ? /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Confirmed request: ",
                            pending.title,
                            ". Retry keeps this exact title, target and publication."
                        ]
                    }) : mode === 'create' ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Opportunity title",
                        htmlFor: `program-follow-on-title-${reference.releaseId}`,
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            id: `program-follow-on-title-${reference.releaseId}`,
                            "data-testid": Testids.landscapes.programFollowOnTitle,
                            maxLength: 500,
                            value: title,
                            onChange: (e)=>setTitle(e.target.value)
                        })
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Existing Opportunity",
                                htmlFor: `program-follow-on-target-${reference.releaseId}`,
                                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    id: `program-follow-on-target-${reference.releaseId}`,
                                    "data-testid": Testids.landscapes.programFollowOnTarget,
                                    value: target,
                                    onChange: (e)=>{
                                        setTarget(e.target.value);
                                        setReview(false);
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Choose an Opportunity"
                                        }),
                                        (!options.isFetching && !options.isError ? options.data?.opportunities ?? [] : []).map((o)=>/*#__PURE__*/ _jsxs("option", {
                                                value: o.opportunityId,
                                                children: [
                                                    o.title,
                                                    o.alreadyLinked ? ' · Already linked' : ''
                                                ]
                                            }, o.opportunityId))
                                    ]
                                })
                            }),
                            selected?.alreadyLinked && /*#__PURE__*/ _jsx("p", {
                                children: "This Opportunity is already linked to this Program. Saving keeps this Opportunity and adds the reviewed publication and Landscape association if needed."
                            }),
                            !review && /*#__PURE__*/ _jsx(SpecraButton, {
                                disabled: !selected || options.isError || options.isFetching,
                                onClick: ()=>setReview(true),
                                children: "Review link"
                            }),
                            review && /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Proposed Opportunity: ",
                                    selected?.title
                                ]
                            })
                        ]
                    }),
                    (review || pending) && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.landscapes.programFollowOnConfirm,
                        disabled: save.isPending || options.isError || options.isFetching || !pending && (mode === 'create' ? !title.trim() : !selected),
                        onClick: confirm,
                        children: save.isPending ? 'Saving Opportunity…' : pending ? 'Retry confirmed save' : 'Confirm save'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.landscapes.programFollowOnBack,
                        disabled: save.isPending,
                        onClick: ()=>{
                            setMode(null);
                            setReview(false);
                            setPending(null);
                            sessionStorage.removeItem(storageKey);
                            save.reset();
                        },
                        children: "Back to follow-on choices"
                    }),
                    pending && /*#__PURE__*/ _jsx("p", {
                        children: "Starting another action may create a separate Opportunity if this request already saved. Retry first to recover its result."
                    }),
                    save.isError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Could not save Opportunity. Access or publication may have changed. Retry the confirmed request, or go back and refresh the reference before a new action."
                    })
                ]
            })
        ]
    });
}
